import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { useAuth } from '@/contexts/AuthContext'
import { usePOSStore, getEffectivePrice, getLineTotalForItem } from '@/store/posStore'
import { useSettingsStore } from '@/store/settingsStore'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { BarcodeScanner } from '@/components/pos/BarcodeScanner'
import { ReceiptModal } from '@/components/pos/ReceiptModal'
import { AdvancePaymentModal } from '@/components/pos/AdvancePaymentModal'
import { HoldTransactionsModal } from '@/components/pos/HoldTransactionsModal'
import { RefundModal } from '@/components/pos/RefundModal'
import { SplitPaymentModal } from '@/components/pos/SplitPaymentModal'
import { CashPaymentModal } from '@/components/pos/CashPaymentModal'
import { StockAlertBanner, InlineStockWarning } from '@/components/pos/StockAlertBanner'
import { BatchSelectionModal } from '@/components/pos/BatchSelectionModal'
import { VariantSelectionModal } from '@/components/pos/VariantSelectionModal'
import { ReturnItemModal } from '@/components/pos/ReturnItemModal'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { apiService } from '@/services/api'
import { Transaction, PaymentSplit, HeldTransaction } from '@/types/pos'
import {
  ScanBarcode,
  Pause,
  RotateCcw,
  Trash2,
  Percent,
  User,
  Wallet,
  Clock,
  CreditCard,
  Banknote,
  Smartphone,
  SplitSquareVertical,
  AlertTriangle,
  X,
  PackageMinus,
  Search,
  LayoutGrid,
  List,
  Star,
  Layers
} from 'lucide-react'

export const POSPage: React.FC = () => {
  const {
    products,
    categories,
    customers,
    isLoading,
    cart,
    selectedCustomer,
    selectedCustomerName,
    discount,
    discountType,
    tax,
    subtotal,
    total,
    heldTransactions,
    lastTransaction,
    stockAlerts,
    fetchCategories,
    fetchProductByBarcode,
    addToCart,
    removeFromCart,
    updateCartItem,
    setCartItemDiscount,
    clearCart,
    setSelectedCustomer,
    setPaymentMethod,
    setPaymentSplits,
    setDiscount,
    processTransaction,
    holdTransaction,
    resumeTransaction,
    deleteHeldTransaction,
    dismissStockAlerts,
    pendingBatchSelection,
    confirmBatchSelection,
    cancelBatchSelection,
    pendingVariantSelection,
    confirmVariantSelection,
    cancelVariantSelection,
    addReturnToCart,
    recordAdvancePayment,
    getAdvanceByRequestId,
    getUnappliedAmountForRequestId,
    quickButtons,
    addQuickButton,
    removeQuickButton,
  } = usePOSStore()

  // Determine selected customer type for customer-aware pricing display
  const selectedCustomerObj = selectedCustomer ? customers.find((c: any) => (c.id || c._id) === selectedCustomer) : null
  const selectedCustomerType = (selectedCustomerObj as any)?.customerType || null

  const [appliedAdvanceReceipts, setAppliedAdvanceReceipts] = useState<Array<{ requestId: string; amount: number; backendId?: string }>>([])
  const [advanceReceiptSearchInput, setAdvanceReceiptSearchInput] = useState('')
  const [advanceLookupResults, setAdvanceLookupResults] = useState<any[]>([])
  const [advanceLookupLoading, setAdvanceLookupLoading] = useState(false)

  const advanceApplied = appliedAdvanceReceipts.reduce((sum, e) => sum + e.amount, 0)
  const balanceDue = Math.max(0, total - advanceApplied)

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [isProcessingLocal, setIsProcessingLocal] = useState(false)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const [showAdvancePaymentModal, setShowAdvancePaymentModal] = useState(false)
  const settings = useSettingsStore(s => s.settings)
  const { isAdmin } = useAuth()
  const [showHeldTransactions, setShowHeldTransactions] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [showReturnItemModal, setShowReturnItemModal] = useState(false)
  const [showSplitPayment, setShowSplitPayment] = useState(false)
  const [showCashPayment, setShowCashPayment] = useState(false)
  const [showHoldInput, setShowHoldInput] = useState(false)
  const [holdName, setHoldName] = useState('')
  const [barcodeLookupLoading, setBarcodeLookupLoading] = useState(false)
  const [localPaymentMethod, setLocalPaymentMethod] = useState<any>(null)
  const [completedTransaction, setCompletedTransaction] = useState<Transaction | null>(null)
  const [showDiscountInput, setShowDiscountInput] = useState(false)
  const [discountInput, setDiscountInput] = useState('')
  const [discountInputType, setDiscountInputType] = useState<'percentage' | 'fixed'>('percentage')
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')

  const { showSuccess, showError, showWarning } = useNotificationActions()

  const handleOpenCashDrawer = () => {
    try {
      const win = window.open('', '_blank', 'width=400,height=300')
      if (!win) {
        showError('Popup blocked', 'Allow popups for this site to open the cash drawer.')
        return
      }

      const storeName = (settings as any)?.receiptHeader || (settings as any)?.storeName || 'POS Terminal'

      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Open Cash Drawer</title>
            <meta charset="utf-8" />
            <style>
              @page { size: 80mm 40mm; margin: 0; }
              body {
                margin: 0;
                padding: 8px;
                font-family: Arial, sans-serif;
                font-size: 10px;
                text-align: center;
              }
              .note {
                margin-top: 8px;
              }
            </style>
          </head>
          <body>
            <div class="note">
              <strong>${storeName}</strong><br/>
              Cash drawer trigger
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 200);
                }, 100);
              };
            </script>
          </body>
        </html>
      `)
      win.document.close()
      showSuccess('Cash Drawer', 'Print job sent to open cash drawer.')
    } catch (err) {
      console.error('Cash drawer open failed', err)
      showError('Cash Drawer Error', 'Could not send open command. Check printer configuration.')
    }
  }

  const filteredCustomers = useMemo(() => {
    const q = customerSearchQuery.trim().toLowerCase()
    if (!q) return customers
    return customers.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && String(c.phone).includes(q))
    )
  }, [customers, customerSearchQuery])

  // Barcode scan handler
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    // Avoid processing if modals are open
    if (showBarcodeScanner || showPaymentModal || showCashPayment || showSplitPayment) return

    setBarcodeLookupLoading(true)
    try {
      // First check if product exists in local products
      const localProduct = products.find(p =>
        p.barcode === barcode ||
        p.isbn === barcode ||
        p.sku === barcode
      )

      if (localProduct) {
        addToCart(localProduct)
        showSuccess('Product Added', `${localProduct.name} added to cart`)

        // Play beep sound
        playBeep()
        return
      }

      // If not found locally, fetch from backend
      const product = await fetchProductByBarcode(barcode)

      if (product) {
        addToCart(product)
        showSuccess('Product Added', `${product.name} added to cart`)
        playBeep()
      } else {
        showError('Product Not Found', `No product found for barcode: ${barcode}`)
        playErrorBeep()
      }
    } catch (error) {
      showError('Scan Error', 'Failed to lookup product')
      playErrorBeep()
    } finally {
      setBarcodeLookupLoading(false)
    }
  }, [products, addToCart, fetchProductByBarcode, showSuccess, showError, showBarcodeScanner, showPaymentModal, showCashPayment, showSplitPayment])

  // USB Barcode Scanner Hook
  useBarcodeScanner({
    onScan: handleBarcodeScan,
    enabled: !showBarcodeScanner && !showPaymentModal && !showCashPayment && !showSplitPayment
  })

  // Sound effects
  const playBeep = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU')
      audio.volume = 0.3
      audio.play().catch(() => { })
    } catch { }
  }

  const playErrorBeep = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YV')
      audio.volume = 0.3
      audio.play().catch(() => { })
    } catch { }
  }

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const filteredProducts = products.filter(product => {
    // Exclude master products for UoM products — POS shows virtual cards (one per sales unit)
    if ((product as any)._isMaster) return false
    // Exclude individual variant members — POS shows group cards instead
    if ((product as any)._isVariantMember) return false
    const nameStr = product.name || ''
    const skuStr = product.sku || ''
    const barcodeStr = product.barcode || ''
    const matchesSearch = nameStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      skuStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      barcodeStr.toLowerCase().includes(searchTerm.toLowerCase())
    const catId = product.category && (typeof product.category === 'string' ? product.category : (product.category.id || product.category._id))
    const matchesCategory = selectedCategory === 'all' || catId === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getDiscountAmount = () => {
    const positiveSubtotal = Math.max(0, subtotal)
    if (discountType === 'percentage') {
      return (positiveSubtotal * discount) / 100
    } else {
      return Math.min(discount, positiveSubtotal) // Fixed amount, but can't exceed subtotal
    }
  }

  const handleSelectPaymentMethod = (method: any) => {
    setLocalPaymentMethod(method)

    if (method.id === 'split') {
      setShowPaymentModal(false)
      setShowSplitPayment(true)
    } else if (method.id === 'cash') {
      setShowPaymentModal(false)
      setShowCashPayment(true)
    }
  }

  const handleCashPaymentComplete = async (amountPaid: number, change: number) => {
    setShowCashPayment(false)
    await processPayment(amountPaid, change, appliedAdvanceReceipts)
  }

  const handleSplitPaymentComplete = async (splits: PaymentSplit[], amountPaid: number, change: number) => {
    setShowSplitPayment(false)
    setPaymentMethod('split')
    setPaymentSplits(splits)
    await processPayment(amountPaid, change, appliedAdvanceReceipts)
  }

  const handleAdvanceLookup = async () => {
    const q = advanceReceiptSearchInput.trim()
    if (!q) {
      showError('Advance receipt', 'Enter a receipt ID or phone number')
      return
    }
    setAdvanceLookupLoading(true)
    try {
      const res = await apiService.get<{ success: boolean; data: any[] }>(`/advance-payments/lookup?q=${encodeURIComponent(q)}`)
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        setAdvanceLookupResults(res.data)
      } else {
        // Fallback to local Zustand lookup
        const advance = getAdvanceByRequestId(q)
        const unapplied = getUnappliedAmountForRequestId(q)
        if (advance && unapplied > 0) {
          handleApplyAdvanceResult({ id: advance.requestId, requestId: advance.requestId, amount: advance.amount, appliedAmount: advance.appliedAmount })
        } else {
          showError('Not found', 'No active advance payment found.')
        }
        setAdvanceLookupResults([])
      }
    } catch {
      // Fallback to local Zustand lookup
      const advance = getAdvanceByRequestId(q)
      const unapplied = getUnappliedAmountForRequestId(q)
      if (advance && unapplied > 0) {
        handleApplyAdvanceResult({ id: advance.requestId, requestId: advance.requestId, amount: advance.amount, appliedAmount: advance.appliedAmount })
      } else {
        showError('Not found', 'No active advance payment found.')
      }
      setAdvanceLookupResults([])
    } finally {
      setAdvanceLookupLoading(false)
    }
  }

  const handleApplyAdvanceResult = (adv: any) => {
    const remaining = (adv.amount || 0) - (adv.appliedAmount || 0)
    if (remaining <= 0) {
      showError('Already applied', 'This advance receipt has no remaining balance.')
      return
    }
    const requestId = adv.requestId
    setAppliedAdvanceReceipts((prev) => {
      const existing = prev.find((e) => e.requestId.toUpperCase() === requestId.toUpperCase())
      if (existing) return prev
      return [...prev, { requestId, amount: remaining, backendId: adv.id }]
    })
    setAdvanceReceiptSearchInput('')
    setAdvanceLookupResults([])
    showSuccess('Advance added', `${requestId} — Rs.${remaining.toFixed(2)} added to bill.`)
  }

  const handleRemoveAdvanceReceipt = (requestId: string) => {
    setAppliedAdvanceReceipts((prev) => prev.filter((e) => e.requestId !== requestId))
  }

  const handleProcessPayment = async () => {
    if (!localPaymentMethod) return

    if (localPaymentMethod.id === 'cash') {
      setShowPaymentModal(false)
      setShowCashPayment(true)
      return
    }

    if (localPaymentMethod.id === 'split') {
      setShowPaymentModal(false)
      setShowSplitPayment(true)
      return
    }

    // For card and digital wallet, process directly with exact amount
    setPaymentMethod(localPaymentMethod.id)
    const advanceTotal = appliedAdvanceReceipts.reduce((s, e) => s + e.amount, 0)
    const exactDue = total - advanceTotal
    await processPayment(exactDue, 0, appliedAdvanceReceipts)
  }

  const processPayment = async (amountPaid?: number, change?: number, advanceReceiptsToApply?: Array<{ requestId: string; amount: number }>) => {
    setIsProcessingLocal(true)
    try {
      const transaction = await processTransaction(amountPaid, change, advanceReceiptsToApply ?? appliedAdvanceReceipts)

      setCompletedTransaction(transaction)
      setShowPaymentModal(false)
      setShowReceiptModal(true)
      setAppliedAdvanceReceipts([])

      showSuccess(
        'Order Successful',
        `Transaction completed for Rs.${total.toFixed(2)}`
      )

      setLocalPaymentMethod(null)
    } catch (error: any) {
      console.error('Payment failed:', error)
      const msg = error?.response?.data?.message || error?.message || 'Check your connection and try again.'
      showError('Payment Failed', msg)
    } finally {
      setIsProcessingLocal(false)
    }
  }

  const handleHoldTransaction = () => {
    if (cart.length === 0) {
      showWarning('Empty Cart', 'Add items to cart before holding')
      return
    }
    setShowHoldInput(true)
  }

  const confirmHoldTransaction = () => {
    holdTransaction(holdName || `Order #${Date.now().toString().slice(-4)}`)
    setShowHoldInput(false)
    setHoldName('')
    showSuccess('Transaction Held', 'You can resume this transaction later')
  }

  const handleResumeTransaction = (heldTx: HeldTransaction) => {
    if (cart.length > 0) {
      if (!confirm('Current cart has items. Replace with held transaction?')) {
        return
      }
    }
    resumeTransaction(heldTx)
    setShowHeldTransactions(false)
    showSuccess('Transaction Resumed', `Resumed: ${heldTx.name}`)
  }

  const handleRefundComplete = (refund: any) => {
    setShowRefundModal(false)
    showSuccess('Refund Processed', `Refund #${refund.refundNumber} completed for Rs.${refund.total.toFixed(2)}`)
  }

  const handleApplyDiscount = () => {
    const value = parseFloat(discountInput)

    if (isNaN(value) || value < 0) {
      showError('Invalid Discount', 'Enter a valid positive number')
      return
    }

    if (discountInputType === 'percentage') {
      if (value > 100) {
        showError('Invalid Discount', 'Percentage cannot exceed 100%')
        return
      }
      setDiscount(value, 'percentage')
      setShowDiscountInput(false)
      setDiscountInput('')
      showSuccess('Discount Applied', `${value}% discount applied`)
    } else {
      // Fixed amount - validate it doesn't exceed subtotal
      if (value > subtotal) {
        showError('Invalid Discount', `Discount cannot exceed subtotal of Rs.${subtotal.toFixed(2)}`)
        return
      }
      setDiscount(value, 'fixed')
      setShowDiscountInput(false)
      setDiscountInput('')
      showSuccess('Discount Applied', `Rs.${value.toFixed(2)} discount applied`)
    }
  }

  const quickActions = [
    {
      label: 'Scan',
      action: () => setShowBarcodeScanner(true),
      icon: <ScanBarcode className="w-5 h-5" />,
      color: 'bg-indigo-500',
      badge: null
    },
    {
      label: 'Hold',
      action: handleHoldTransaction,
      icon: <Pause className="w-5 h-5" />,
      color: 'bg-yellow-500',
      badge: heldTransactions.length > 0 ? heldTransactions.length : null
    },
    {
      label: 'Held',
      action: () => setShowHeldTransactions(true),
      icon: <Clock className="w-5 h-5" />,
      color: 'bg-purple-500',
      badge: heldTransactions.length > 0 ? heldTransactions.length : null
    },
    {
      label: 'Refund',
      action: () => setShowRefundModal(true),
      icon: <RotateCcw className="w-5 h-5" />,
      color: 'bg-orange-500',
      badge: null
    },
    {
      label: 'Return',
      action: () => setShowReturnItemModal(true),
      icon: <PackageMinus className="w-5 h-5" />,
      color: 'bg-amber-600',
      badge: cart.filter(i => i.isReturn).length > 0 ? cart.filter(i => i.isReturn).length : null
    },
    {
      label: 'Discount',
      action: () => {
        setDiscountInputType(discountType) // Set to current type
        setShowDiscountInput(true)
      },
      icon: <Percent className="w-5 h-5" />,
      color: 'bg-green-500',
      badge: discount > 0 ? (discountType === 'percentage' ? `${discount}%` : `Rs.${discount.toFixed(2)}`) : null
    },
    {
      label: 'Clear',
      action: () => { if (confirm('Clear all items from cart?')) clearCart() },
      icon: <Trash2 className="w-5 h-5" />,
      color: 'bg-red-500',
      badge: null
    },
    {
      label: 'Customer',
      action: () => setShowCustomerModal(true),
      icon: <User className="w-5 h-5" />,
      color: 'bg-cyan-500',
      badge: selectedCustomer ? '✓' : null
    },
    {
      label: 'Advance Payment',
      action: () => setShowAdvancePaymentModal(true),
      icon: <Wallet className="w-5 h-5" />,
      color: 'bg-blue-500',
      badge: null
    },
    ...(isAdmin ? [{
      label: 'Open Drawer',
      action: handleOpenCashDrawer,
      icon: <Banknote className="w-5 h-5" />,
      color: 'bg-slate-600',
      badge: null as any
    }] : [])
  ]

  const paymentMethods: any[] = [
    { id: 'cash', name: 'Cash', type: 'cash', icon: <Banknote className="w-6 h-6" /> },
    { id: 'card', name: 'Card', type: 'card', icon: <CreditCard className="w-6 h-6" /> },
    { id: 'wallet', name: 'Wallet', type: 'digital', icon: <Smartphone className="w-6 h-6" /> },
    { id: 'split', name: 'Split', type: 'split', icon: <SplitSquareVertical className="w-6 h-6" /> }
  ]

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6 pb-20 sm:pb-24">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold esales-text font-display">Sachin Bookshop Terminal</h1>
            <p className="text-muted-foreground mt-1 text-xs sm:text-sm">Bookstore Checkout Assistant</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-xs sm:text-sm text-muted-foreground">
              {new Date().toLocaleDateString()}
            </div>
            <div className="hidden sm:flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full animate-pulse ${barcodeLookupLoading ? 'bg-yellow-400' : 'bg-green-400'}`}></div>
              <span className="text-sm text-muted-foreground">
                {barcodeLookupLoading ? 'Scanning...' : 'Scanner Ready'}
              </span>
            </div>
          </div>
        </div>

        {/* Stock Alerts */}
        <StockAlertBanner
          alerts={stockAlerts}
          onDismissAll={dismissStockAlerts}
        />

        {/* Quick Actions – Sub Navbar fixed to bottom */}
        <div className="fixed inset-x-0 bottom-0 z-30 bg-background/90 border-t border-border backdrop-blur-sm">
          <div className="w-full px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-3">
            <h3 className="hidden md:block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
              Quick Actions
            </h3>
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-2 justify-start md:justify-center pb-1">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    disabled={(action as any).disabled}
                    className={`${action.color} text-white px-3 py-2 rounded-lg hover:opacity-90 transition-all flex items-center gap-1.5 relative disabled:opacity-40 disabled:cursor-not-allowed text-[11px] font-semibold`}
                  >
                    {action.badge && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-black text-[9px] font-bold rounded-full flex items-center justify-center">
                        {action.badge}
                      </span>
                    )}
                    <span className="w-4 h-4 flex items-center justify-center">
                      {action.icon}
                    </span>
                    <span className="uppercase hidden sm:inline-block">
                      {action.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products Section */}
          <div className="lg:col-span-2">
            <div className="glass-card">
              {/* Search and Filters */}
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search by title, SKU, or barcode..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                    <button
                      onClick={() => setShowBarcodeScanner(true)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-muted rounded-lg transition-colors"
                      title="Scan Barcode"
                    >
                      <ScanBarcode className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                {/* Quick Access Buttons */}
                {quickButtons.length > 0 && (
                  <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-3">
                    <span className="text-[11px] text-muted-foreground font-bold uppercase tracking-wider whitespace-nowrap">Quick:</span>
                    {quickButtons.map(qb => {
                      const qProduct = products.find(p => (p.id || p._id) === qb.productId)
                      if (!qProduct) return null
                      return (
                        <button
                          key={qb.id}
                          onClick={() => addToCart(qProduct)}
                          disabled={qProduct.stock <= 0}
                          className="px-3 py-1.5 bg-primary/15 border border-primary/30 rounded-lg text-sm font-semibold hover:bg-primary/25 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                          title={`Stock: ${qProduct.stock}`}
                        >
                          {qb.productName}
                        </button>
                      )
                    })}
                  </div>
                )}

                <div className="flex space-x-2 overflow-x-auto pb-2 min-w-0">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${selectedCategory === 'all' ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
                  >
                    All Products
                  </button>
                  {categories.map(c => (
                    <button
                      key={c.id || (c as any)._id}
                      onClick={() => setSelectedCategory(c.id || (c as any)._id)}
                      className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${selectedCategory === (c.id || (c as any)._id) ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-muted-foreground font-semibold">View:</span>
                  <div className="flex bg-muted rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'grid'
                          ? 'bg-primary text-white'
                          : 'text-muted-foreground hover:text-foreground'
                        }`}
                      title="Grid view with images"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      Grid
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list'
                          ? 'bg-primary text-white'
                          : 'text-muted-foreground hover:text-foreground'
                        }`}
                      title="List view without images"
                    >
                      <List className="w-4 h-4" />
                      Detailed
                    </button>
                  </div>
                </div>
              </div>

              {/* Detailed Items */}
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 max-h-[55vh] overflow-y-auto pr-2">
                  {isLoading ? (
                    <div className="col-span-full">
                      <LoadingSpinner
                        size="md"
                        message="Loading Products"
                        submessage="Fetching your collection..."
                      />
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="col-span-full py-20 text-center text-muted-foreground">
                      <p>No books found for this search.</p>
                    </div>
                  ) : (
                    filteredProducts.map(product => {
                      const pid = (product.id || product._id) ?? ''
                      const inCartQty = cart.find(item => (item.product.id || item.product._id) === pid)?.quantity || 0
                      const isLowStock = product.stock <= product.minStock
                      const isOutOfStock = product.stock <= 0
                      const isPinned = quickButtons.some(q => q.productId === pid)

                      return (
                        <div
                          key={pid}
                          onClick={() => !isOutOfStock && addToCart(product)}
                          className={`glass-card hover-lift text-left group flex flex-col items-center justify-center p-4 relative cursor-pointer ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {/* Pin / Quick Button toggle */}
                          <button
                            onClick={(e) => { e.stopPropagation(); isPinned ? removeQuickButton(pid) : addQuickButton(pid) }}
                            className="absolute top-1 left-1 p-1 rounded-md hover:bg-muted/50 transition-colors z-10"
                            title={isPinned ? 'Remove from quick access' : 'Add to quick access'}
                          >
                            <Star className={`w-3 h-3 ${isPinned ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40 hover:text-yellow-400'}`} />
                          </button>

                          {/* Stock Badge */}
                          {isLowStock && !isOutOfStock && (
                            <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-500 text-[10px] font-bold rounded">
                              Low Stock
                            </span>
                          )}
                          {isOutOfStock && (
                            <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-red-500/20 text-red-500 text-[10px] font-bold rounded">
                              Out of Stock
                            </span>
                          )}

                          {/* In Cart Badge */}
                          {inCartQty > 0 && (
                            <span className="absolute top-2 left-2 w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center">
                              {inCartQty}
                            </span>
                          )}

                          {/* Variant Group Badge (only for non-group-card products) */}
                          {product.variantGroupId && !(product as any)._isVariantGroupCard && (
                            <span className="absolute top-2 right-2 p-1 bg-violet-500/20 text-violet-400 rounded" title={product.variantGroupName || 'Variant'}>
                              <Layers className="w-3 h-3" />
                            </span>
                          )}

                          <div className="w-16 h-20 mb-3 group-hover:scale-105 transition-transform flex items-center justify-center">
                            {(product as any)._isVariantGroupCard ? (
                              <div className="w-14 h-14 rounded-xl bg-violet-500/20 flex items-center justify-center">
                                <Layers className="w-8 h-8 text-violet-400" />
                              </div>
                            ) : (product.image?.startsWith('http') || product.image?.startsWith('data:')) ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover rounded-lg shadow"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                    ; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden')
                                }}
                              />
                            ) : null}
                            {!(product as any)._isVariantGroupCard && (
                              <span className={`text-4xl ${(product.image?.startsWith('http') || product.image?.startsWith('data:')) ? 'hidden' : ''}`}>
                                {product.image && !product.image.startsWith('http') && !product.image.startsWith('data:') ? product.image : '📖'}
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-foreground text-center line-clamp-1 w-full text-sm">{product.name}</h3>
                          {(product as any)._isVariantGroupCard && (
                            <p className="text-[10px] text-violet-400 mt-0.5">{product.description}</p>
                          )}
                          {(product as any)._isVariantGroupCard ? (
                            <p className="text-violet-400 text-xs font-semibold mt-1">{product.stock.toLocaleString()} total stock</p>
                          ) : selectedCustomerType === 'wholesale' && product.wholesalePrice && product.wholesalePrice > 0 ? (
                            <div className="flex items-center gap-1.5 mt-1">
                              <p className="text-blue-400 font-bold">Rs.{product.wholesalePrice.toFixed(2)}</p>
                              <p className="text-[9px] text-muted-foreground">WS</p>
                            </div>
                          ) : (selectedCustomerType === 'regular' || selectedCustomerType === 'special') && product.discountPrice && product.discountPrice > 0 ? (
                            <div className="flex items-center gap-1.5 mt-1">
                              <p className={`font-bold ${selectedCustomerType === 'special' ? 'text-purple-400' : 'text-yellow-400'}`}>Rs.{product.discountPrice.toFixed(2)}</p>
                              <p className="text-[10px] text-muted-foreground line-through">Rs.{(product.price ?? 0).toFixed(2)}</p>
                            </div>
                          ) : product.price === 0 ? (
                            <p className="text-muted-foreground text-sm font-medium mt-1 italic">No Price</p>
                          ) : (
                            <p className="text-primary font-bold mt-1">Rs.{(product.price ?? 0).toFixed(2)}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1 opacity-70">
                            {typeof product.category === 'string' ? product.category : product.category?.name || 'Book'}
                          </p>
                          <p className={`text-[10px] font-medium tabular-nums mt-0.5 ${
                            isOutOfStock ? 'text-red-500' : isLowStock ? 'text-amber-500' : 'text-muted-foreground'
                          }`}>
                            Stock: {typeof product.stock === 'number' ? product.stock : 0}
                          </p>
                          {product.barcode && (
                            <p className="text-[9px] text-muted-foreground font-mono mt-0.5">{product.barcode}</p>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              ) : (
                /* Detailed View */
                <div className="max-h-[55vh] overflow-y-auto pr-2 space-y-2">
                  {/* Table Header */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2 text-[11px] font-bold text-muted-foreground uppercase tracking-wider border-b border-border">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="flex-1 min-w-0">Name</span>
                      <span className="hidden md:block w-24">Category</span>
                      <span className="hidden lg:block w-24">Barcode</span>
                      <span className="w-14">Stock</span>
                    </div>
                    <span className="w-20 text-right">Price</span>
                  </div>
                  {isLoading ? (
                    <div className="py-12">
                      <LoadingSpinner
                        size="md"
                        message="Loading Products"
                        submessage="Fetching your collection..."
                      />
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground">
                      <p>No books found for this search.</p>
                    </div>
                  ) : (
                    filteredProducts.map(product => {
                      const lpid = (product.id || product._id) ?? ''
                      const inCartQty = cart.find(item => (item.product.id || item.product._id) === lpid)?.quantity || 0
                      const isLowStock = product.stock <= product.minStock
                      const isOutOfStock = product.stock <= 0
                      const isListPinned = quickButtons.some(q => q.productId === lpid)

                      return (
                        <div
                          key={lpid}
                          onClick={() => !isOutOfStock && addToCart(product)}
                          className={`w-full text-left glass-card p-3 flex items-center justify-between gap-3 hover:brightness-110 transition-all cursor-pointer ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Name */}
                            <h3 className="font-bold text-sm truncate text-foreground flex-1 min-w-0 flex items-center gap-1.5">
                              {product.name}
                              {product.variantGroupId && <Layers className="w-3 h-3 text-violet-400 flex-shrink-0" />}
                              {isLowStock && !isOutOfStock && (
                                <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-500 text-[10px] font-bold rounded flex-shrink-0">
                                  Low
                                </span>
                              )}
                              {isOutOfStock && (
                                <span className="px-1.5 py-0.5 bg-red-500/20 text-red-500 text-[10px] font-bold rounded flex-shrink-0">
                                  Out
                                </span>
                              )}
                            </h3>

                            {/* Category */}
                            <p className="hidden md:block text-muted-foreground uppercase tracking-wider truncate w-24 flex-shrink-0 text-xs">
                              {typeof product.category === 'string'
                                ? product.category
                                : product.category?.name || 'Book'}
                            </p>

                            {/* Barcode — always render to keep alignment */}
                            <p className="hidden lg:block text-xs text-muted-foreground font-mono truncate w-24 flex-shrink-0">
                              {product.barcode || '—'}
                            </p>

                            {/* Stock */}
                            <span
                              className={`text-xs font-medium tabular-nums w-14 flex-shrink-0 ${
                                isOutOfStock
                                  ? 'text-red-500'
                                  : isLowStock
                                    ? 'text-amber-500'
                                    : 'text-muted-foreground'
                              }`}
                            >
                              {typeof product.stock === 'number' ? product.stock : 0}
                            </span>
                          </div>

                          {/* Price */}
                          <div className="w-20 text-right flex-shrink-0">
                            {selectedCustomerType === 'wholesale' && product.wholesalePrice && product.wholesalePrice > 0 ? (
                              <p className="text-blue-400 font-bold text-sm">Rs.{product.wholesalePrice.toFixed(2)}</p>
                            ) : (selectedCustomerType === 'regular' || selectedCustomerType === 'special') && product.discountPrice && product.discountPrice > 0 ? (
                              <div>
                                <p className={`font-bold text-sm ${selectedCustomerType === 'special' ? 'text-purple-400' : 'text-yellow-400'}`}>Rs.{product.discountPrice.toFixed(2)}</p>
                                <p className="text-[10px] text-muted-foreground line-through">Rs.{(product.price ?? 0).toFixed(2)}</p>
                              </div>
                            ) : product.price === 0 ? (
                              <p className="text-muted-foreground font-medium text-sm italic">No Price</p>
                            ) : (
                              <p className="text-primary font-bold text-sm">Rs.{(product.price ?? 0).toFixed(2)}</p>
                            )}
                          </div>

                          {/* In Cart Badge + Pin */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {inCartQty > 0 && (
                              <span className="w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center">
                                {inCartQty}
                              </span>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); isListPinned ? removeQuickButton(lpid) : addQuickButton(lpid) }}
                              className="p-1 rounded-md hover:bg-muted/50 transition-colors flex-shrink-0"
                              title={isListPinned ? 'Remove from quick access' : 'Add to quick access'}
                            >
                              <Star className={`w-3.5 h-3.5 ${isListPinned ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40 hover:text-yellow-400'}`} />
                            </button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Cart Section */}
          <div className="space-y-6">
            <div className="glass-card sticky top-6">
              <div className="flex justify-between items-center mb-4 border-b border-border pb-4">
                <h3 className="text-xl font-bold">Shopping Cart</h3>
                <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold">{cart.length} ITEMS</span>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground opacity-50">
                  <div className="text-5xl mb-4">🛒</div>
                  <p className="font-medium">Ready for checkout</p>
                  <p className="text-xs mt-1">Scan or select products to add</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                  {cart.map(item => {
                    const product = products.find(p => (p.id || p._id) === (item.product.id || item.product._id)) || item.product
                    const isReturnItem = item.isReturn

                    return (
                      <div key={`${item.product.id || (item.product as any)._id}-${item.batchId || 'default'}-${item.isReturn ? 'return' : 'sale'}-${item.returnFromTransactionId || ''}`} className={`p-3 rounded-xl group relative ${isReturnItem ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-muted/20'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 truncate mr-2">
                            <div className="w-10 h-12 flex-shrink-0 flex items-center justify-center">
                              {isReturnItem ? (
                                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                                  <PackageMinus className="w-5 h-5 text-orange-400" />
                                </div>
                              ) : (
                                <>
                                  {(item.product.image?.startsWith('http') || item.product.image?.startsWith('data:')) ? (
                                    <img
                                      src={item.product.image}
                                      alt={item.product.name}
                                      className="w-full h-full object-cover rounded shadow"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none'
                                          ; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden')
                                      }}
                                    />
                                  ) : null}
                                  <span className={`text-2xl ${(item.product.image?.startsWith('http') || item.product.image?.startsWith('data:')) ? 'hidden' : ''}`}>
                                    {item.product.image && !item.product.image.startsWith('http') && !item.product.image.startsWith('data:') ? item.product.image : '📖'}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="truncate">
                              <p className={`font-bold truncate text-sm ${isReturnItem ? 'text-orange-400' : 'text-foreground'}`}>{item.product.name}</p>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className={`text-xs font-bold ${isReturnItem ? 'text-orange-400' : 'text-primary'}`}>
                                  {isReturnItem ? '-' : ''}Rs.{getEffectivePrice(item).toFixed(2)}
                                  {!isReturnItem && (product as any).volumePricing?.rules?.length ? (
                                    <span className="text-[10px] font-normal text-muted-foreground ml-1">/unit</span>
                                  ) : null}
                                </p>
                                <p className="text-xs font-bold text-foreground">
                                  = Rs.{getLineTotalForItem(item).toFixed(2)}
                                </p>
                                {isReturnItem && (
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 leading-none">
                                    Return
                                  </span>
                                )}
                                {item.batchId && !isReturnItem && (
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 leading-none">
                                    Batch
                                  </span>
                                )}
                                {item.salesUnitName && !isReturnItem && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 leading-none">
                                    {item.salesUnitName}
                                  </span>
                                )}
                              </div>
                              {isReturnItem && item.returnFromReceiptNumber && (
                                <p className="text-[10px] text-muted-foreground truncate">
                                  from #{item.returnFromReceiptNumber.length > 18 ? item.returnFromReceiptNumber.slice(0, 18) + '...' : item.returnFromReceiptNumber}
                                </p>
                              )}
                              {!isReturnItem && (
                                <InlineStockWarning
                                  currentStock={product.stock}
                                  minStock={product.minStock}
                                  quantityInCart={item.quantity}
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0 bg-background/50 rounded-lg p-1">
                            {!isReturnItem && (
                              <>
                                <button
                                  onClick={() => updateCartItem(item.product.id || (item.product as any)._id, item.quantity - 1, item.batchId)}
                                  className="w-7 h-7 bg-muted rounded-md flex items-center justify-center hover:bg-muted/80"
                                >-</button>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value)
                                    if (isNaN(val) || val < 1) return
                                    updateCartItem(item.product.id || (item.product as any)._id, val, item.batchId)
                                  }}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value)
                                    if (isNaN(val) || val < 1) {
                                      updateCartItem(item.product.id || (item.product as any)._id, 1, item.batchId)
                                    }
                                  }}
                                  className="w-10 text-center text-xs font-bold bg-transparent border border-border/50 rounded-md py-1 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                />
                                <button
                                  onClick={() => updateCartItem(item.product.id || (item.product as any)._id, item.quantity + 1, item.batchId)}
                                  className="w-7 h-7 bg-muted rounded-md flex items-center justify-center hover:bg-muted/80"
                                >+</button>
                              </>
                            )}
                            {isReturnItem && (
                              <span className="px-2 text-xs font-bold text-orange-400">x{item.quantity}</span>
                            )}
                            <button
                              onClick={() => {
                                if (isReturnItem) {
                                  // For return items, filter by transaction ID to avoid removing wrong items
                                  const { cart } = usePOSStore.getState()
                                  const updated = cart.filter(ci =>
                                    !(ci.isReturn && (ci.product.id || ci.product._id) === (item.product.id || (item.product as any)._id) && ci.returnFromTransactionId === item.returnFromTransactionId)
                                  )
                                  usePOSStore.setState({ cart: updated })
                                  usePOSStore.getState().calculateTotals()
                                } else {
                                  removeFromCart(item.product.id || (item.product as any)._id, item.batchId)
                                }
                              }}
                              className="w-7 h-7 text-red-400 hover:text-red-500 flex items-center justify-center ml-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {/* Per-item discount % - only for non-return items */}
                        {!isReturnItem && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/30">
                            <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-wider">Item disc %</span>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={item.discount ?? 0}
                              onChange={(e) => {
                                const pid = item.product.id || (item.product as any)._id
                                const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                setCartItemDiscount(pid, isNaN(v) ? 0 : v, item.batchId)
                              }}
                              onBlur={(e) => {
                                const pid = item.product.id || (item.product as any)._id
                                const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                const clamped = Math.max(0, Math.min(100, isNaN(v) ? 0 : v))
                                setCartItemDiscount(pid, clamped, item.batchId)
                              }}
                              className="w-12 px-1.5 py-0.5 text-xs text-center bg-background/50 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Totals */}
              <div className="mt-6 pt-4 border-t border-dashed border-border">
                <div className="space-y-2 mb-4 text-sm">
                  {(() => {
                    const returnCredit = cart.filter(i => i.isReturn).reduce((sum, i) => sum + getLineTotalForItem(i), 0)
                    const purchaseSubtotal = cart.filter(i => !i.isReturn).reduce((sum, i) => sum + getLineTotalForItem(i), 0)
                    return (
                      <>
                        <div className="flex justify-between opacity-70">
                          <span>Subtotal</span>
                          <span>Rs.{purchaseSubtotal.toFixed(2)}</span>
                        </div>
                        {returnCredit > 0 && (
                          <div className="flex justify-between text-orange-400 font-bold">
                            <span>Return Credit ({cart.filter(i => i.isReturn).length} item{cart.filter(i => i.isReturn).length !== 1 ? 's' : ''})</span>
                            <span>-Rs.{returnCredit.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )
                  })()}
                  {discount > 0 && (
                    <div className="flex justify-between text-green-400 font-bold">
                      <span>Discount {discountType === 'percentage' ? `(${discount}%)` : `(Rs.${discount.toFixed(2)})`}</span>
                      <span>-Rs.{getDiscountAmount().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-2xl font-bold mt-4 pt-4 border-t border-border">
                    <span className="esales-text font-display">Total</span>
                    <span className={total < 0 ? 'text-orange-400' : 'text-primary'}>
                      {total < 0 ? '-' : ''}Rs.{Math.abs(total).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Customer Selection Display */}
                <div
                  className="p-3 bg-primary/5 border border-primary/20 rounded-xl mb-4 flex items-center justify-between group cursor-pointer hover:bg-primary/10 transition-colors"
                  onClick={() => setShowCustomerModal(true)}
                >
                  <div className="truncate mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-[10px] text-primary font-bold uppercase tracking-widest">Customer</p>
                      {selectedCustomerType === 'wholesale' && (
                        <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-blue-400/15 text-blue-400 border border-blue-400/30">Wholesale</span>
                      )}
                      {selectedCustomerType === 'regular' && (
                        <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-green-400/15 text-green-400 border border-green-400/30">Regular</span>
                      )}
                      {selectedCustomerType === 'special' && (
                        <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-purple-400/15 text-purple-400 border border-purple-400/30">Special</span>
                      )}
                    </div>
                    <p className="font-bold text-foreground truncate text-sm">
                      {selectedCustomerName || (selectedCustomer ? (customers.find(c => (c.id || c._id) === selectedCustomer)?.name || 'Loading...') : 'Guest')}
                    </p>
                  </div>
                  <div className="bg-primary text-white p-2 rounded-lg group-hover:scale-110 transition-transform">
                    <User className="w-4 h-4" />
                  </div>
                </div>

                {/* Stock Warning */}
                {stockAlerts.some(a => a.severity === 'critical') && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <p className="text-xs text-red-500 font-semibold">Some items exceed available stock!</p>
                  </div>
                )}

                {/* Checkout Button */}
                <button
                  onClick={() => setShowPaymentModal(true)}
                  disabled={cart.length === 0 || isProcessingLocal || stockAlerts.some(a => a.severity === 'critical')}
                  className="w-full glow-button py-4 text-lg font-bold tracking-widest disabled:opacity-50"
                >
                  {isProcessingLocal ? 'PROCESSING...' : 'CHECKOUT'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Modal */}
        {showCustomerModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-md w-full border-primary/30 flex flex-col max-h-[85vh]">
              <h3 className="text-2xl font-bold mb-4 esales-text">Select Customer</h3>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  placeholder="Search by name, email or phone..."
                  className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-xl text-sm placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-shadow"
                  autoFocus
                />
                {customerSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setCustomerSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-2 custom-scrollbar">
                <button
                  onClick={() => { setSelectedCustomer(null, null); setShowCustomerModal(false); setCustomerSearchQuery(''); }}
                  className="w-full text-left p-4 rounded-xl bg-primary/10 border-2 border-primary/20 hover:bg-primary/20 transition-all font-bold flex-shrink-0"
                >
                  Guest Purchase
                </button>
                {filteredCustomers.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">
                    {customerSearchQuery ? 'No customers match your search.' : 'No customers yet.'}
                  </div>
                ) : (
                  filteredCustomers.map((c) => (
                    <button
                      key={c.id || c._id}
                      onClick={() => { setSelectedCustomer(c.id || c._id, c.name); setShowCustomerModal(false); setCustomerSearchQuery(''); }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex-shrink-0 ${selectedCustomer === (c.id || c._id) ? 'border-primary bg-primary/10' : 'border-transparent bg-muted/40 hover:bg-muted/60'}`}
                    >
                      <p className="font-bold">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email || c.phone || 'No contact info'}</p>
                    </button>
                  ))
                )}
              </div>
              <button
                onClick={() => { setShowCustomerModal(false); setCustomerSearchQuery(''); }}
                className="w-full py-3 mt-4 bg-muted hover:bg-muted/80 rounded-xl font-bold uppercase tracking-widest text-xs flex-shrink-0"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-md w-full border-primary/30 max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-6 esales-text text-center">Checkout</h3>
              <div className="p-6 bg-primary/10 rounded-2xl mb-4 text-center ring-1 ring-primary/20">
                <p className="text-[10px] text-primary font-bold uppercase tracking-widest mb-1">Grand Total</p>
                <p className="text-5xl font-black text-primary font-display">Rs.{total.toFixed(2)}</p>
                {advanceApplied > 0 && (
                  <div className="mt-3 pt-3 border-t border-primary/20 space-y-1">
                    <p className="text-xs text-green-600 font-semibold">Advance applied: Rs.{advanceApplied.toFixed(2)}</p>
                    <p className="text-sm font-bold text-primary">Balance due: Rs.{balanceDue.toFixed(2)}</p>
                  </div>
                )}
              </div>
              {/* Advance receipt search - add advance to bill */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Apply advance payment</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={advanceReceiptSearchInput}
                    onChange={(e) => setAdvanceReceiptSearchInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdvanceLookup() } }}
                    placeholder="Receipt ID or phone number"
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAdvanceLookup}
                    disabled={advanceLookupLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 whitespace-nowrap disabled:opacity-50"
                  >
                    {advanceLookupLoading ? '...' : 'Search'}
                  </button>
                </div>
                {advanceLookupResults.length > 0 && (
                  <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {advanceLookupResults.map((adv: any) => {
                      const remaining = (adv.amount || 0) - (adv.appliedAmount || 0)
                      return (
                        <li key={adv.id} className="flex items-center justify-between text-xs bg-muted/50 rounded-lg px-3 py-2 cursor-pointer hover:bg-muted/80" onClick={() => handleApplyAdvanceResult(adv)}>
                          <div>
                            <span className="font-mono">{adv.requestId}</span>
                            <span className="text-muted-foreground ml-2">{adv.customerName || adv.customerPhone || ''}</span>
                          </div>
                          <span className="font-bold text-green-600 whitespace-nowrap ml-2">Rs.{remaining.toFixed(2)}</span>
                        </li>
                      )
                    })}
                  </ul>
                )}
                {appliedAdvanceReceipts.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {appliedAdvanceReceipts.map(({ requestId, amount }) => (
                      <li key={requestId} className="flex items-center justify-between text-xs bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                        <span className="font-mono truncate">{requestId}</span>
                        <span className="font-bold text-green-600 whitespace-nowrap ml-2">Rs.{amount.toFixed(2)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdvanceReceipt(requestId)}
                          className="p-1 text-red-500 hover:bg-red-500/10 rounded ml-1"
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {paymentMethods.map(m => (
                  <button
                    key={m.id}
                    onClick={() => handleSelectPaymentMethod(m)}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center group ${localPaymentMethod?.id === m.id ? 'border-primary bg-primary/10 ring-4 ring-primary/5' : 'border-transparent bg-muted/20 hover:bg-muted/40'}`}
                  >
                    <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">{m.icon}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest">{m.name}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => { setShowPaymentModal(false); setLocalPaymentMethod(null); setAppliedAdvanceReceipts([]); setAdvanceReceiptSearchInput(''); }}
                  className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest"
                >
                  Back
                </button>
                <button
                  onClick={handleProcessPayment}
                  disabled={!localPaymentMethod || isProcessingLocal}
                  className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect disabled:opacity-50"
                >
                  {isProcessingLocal ? 'PROCESSING...' : 'COMPLETE'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hold Transaction Input */}
        {showHoldInput && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-sm w-full">
              <h3 className="text-xl font-bold mb-4 esales-text">Hold Transaction</h3>
              <p className="text-sm text-muted-foreground mb-4">Give this order a name so you can find it later</p>
              <input
                type="text"
                value={holdName}
                onChange={(e) => setHoldName(e.target.value)}
                placeholder="e.g., Table 5, John's order..."
                className="w-full px-4 py-3 bg-background border border-border rounded-lg mb-4 focus:ring-2 focus:ring-primary focus:outline-none"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && confirmHoldTransaction()}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowHoldInput(false); setHoldName(''); }}
                  className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmHoldTransaction}
                  className="flex-1 py-3 bg-yellow-500 text-white hover:bg-yellow-600 rounded-xl font-semibold text-sm"
                >
                  Hold Order
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Discount Input Modal */}
        {showDiscountInput && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-md w-full">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold esales-text">Apply Discount</h3>
                <button
                  onClick={() => { setShowDiscountInput(false); setDiscountInput(''); }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Discount Type Toggle */}
              <div className="mb-4">
                <label className="text-xs font-bold text-muted-foreground uppercase mb-2 block">Discount Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setDiscountInputType('percentage')
                      setDiscountInput('')
                    }}
                    className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${discountInputType === 'percentage'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                      }`}
                  >
                    Percentage (%)
                  </button>
                  <button
                    onClick={() => {
                      setDiscountInputType('fixed')
                      setDiscountInput('')
                    }}
                    className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${discountInputType === 'fixed'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                      }`}
                  >
                    Fixed Amount (Rs.)
                  </button>
                </div>
              </div>

              {/* Discount Input */}
              <div className="relative mb-4">
                <input
                  type="number"
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  placeholder={discountInputType === 'percentage' ? 'Enter discount percentage...' : 'Enter discount amount...'}
                  min="0"
                  max={discountInputType === 'percentage' ? '100' : subtotal.toFixed(2)}
                  step={discountInputType === 'percentage' ? '1' : '0.01'}
                  className="w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {discountInputType === 'percentage' ? '%' : 'Rs.'}
                </span>
              </div>

              {/* Quick Select Buttons */}
              {discountInputType === 'percentage' ? (
                <div className="flex gap-2 mb-4">
                  {[5, 10, 15, 20, 25].map(val => (
                    <button
                      key={val}
                      onClick={() => setDiscountInput(val.toString())}
                      className="flex-1 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-semibold"
                    >
                      {val}%
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2 mb-4">
                  {[10, 25, 50, 100].map(val => (
                    <button
                      key={val}
                      onClick={() => setDiscountInput(val.toString())}
                      className="flex-1 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={val > subtotal}
                    >
                      Rs.{val}
                    </button>
                  ))}
                </div>
              )}

              {/* Current Subtotal Info */}
              {discountInputType === 'fixed' && (
                <div className="mb-4 p-3 bg-muted/20 rounded-lg text-sm">
                  <p className="text-muted-foreground">Subtotal: <span className="font-semibold text-foreground">Rs.{subtotal.toFixed(2)}</span></p>
                  <p className="text-xs text-muted-foreground mt-1">Maximum discount: Rs.{subtotal.toFixed(2)}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDiscountInput(false); setDiscountInput(''); }}
                  className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyDiscount}
                  className="flex-1 py-3 bg-green-500 text-white hover:bg-green-600 rounded-xl font-semibold text-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Scanner Modal */}
        {showBarcodeScanner && (
          <BarcodeScanner
            onScan={(barcode) => {
              handleBarcodeScan(barcode)
              setShowBarcodeScanner(false)
            }}
            onClose={() => setShowBarcodeScanner(false)}
            isLoading={barcodeLookupLoading}
          />
        )}

        {/* Advance Payment Request Modal */}
        {showAdvancePaymentModal && (
          <AdvancePaymentModal
            cart={cart}
            subtotal={subtotal}
            discount={discount}
            discountType={discountType}
            tax={tax}
            total={total}
            customerName={selectedCustomerName}
            customerId={selectedCustomer}
            customerPhone={(selectedCustomerObj as any)?.phone || null}
            storeName={settings.receiptHeader || settings.storeName}
            storeAddress={settings.storeAddress}
            storePhone={settings.storePhone}
            storeEmail={settings.storeEmail}
            onRecordAdvance={({ requestId, amount, customerPhone }) => {
              recordAdvancePayment({
                requestId,
                amount,
                totalAtRequest: total,
                customerPhone,
                customerName: selectedCustomerName || undefined,
                customerId: selectedCustomer,
                items: cart.filter(i => !i.isReturn).map(i => ({
                  productName: i.product.name || 'Item',
                  quantity: i.quantity,
                  price: getLineTotalForItem(i),
                })),
              })
            }}
            onClose={() => setShowAdvancePaymentModal(false)}
          />
        )}

        {/* Receipt Modal */}
        {showReceiptModal && (completedTransaction || lastTransaction) && (
          <ReceiptModal
            transaction={completedTransaction || lastTransaction!}
            storeName={settings.receiptHeader || settings.storeName}
            storeAddress={settings.storeAddress}
            storePhone={settings.storePhone}
            storeEmail={settings.storeEmail}
            receiptFooter={settings.receiptFooter}
            onClose={() => {
              setShowReceiptModal(false)
              setCompletedTransaction(null)
            }}
            onPrint={() => { }}
            onEmail={() => showSuccess('Email Sent', 'Receipt sent to customer email')}
            onNewTransaction={() => {
              clearCart()
              setShowReceiptModal(false)
              setCompletedTransaction(null)
            }}
          />
        )}

        {/* Held Transactions Modal */}
        {showHeldTransactions && (
          <HoldTransactionsModal
            heldTransactions={heldTransactions}
            onResume={handleResumeTransaction}
            onDelete={deleteHeldTransaction}
            onClose={() => setShowHeldTransactions(false)}
          />
        )}

        {/* Refund Modal */}
        {showRefundModal && (
          <RefundModal
            onClose={() => setShowRefundModal(false)}
            onRefundComplete={handleRefundComplete}
          />
        )}

        {/* Return Item Modal */}
        {showReturnItemModal && (
          <ReturnItemModal
            onAddReturn={addReturnToCart}
            onClose={() => setShowReturnItemModal(false)}
          />
        )}

        {/* Split Payment Modal */}
        {showSplitPayment && (
          <SplitPaymentModal
            total={balanceDue}
            onComplete={handleSplitPaymentComplete}
            onClose={() => setShowSplitPayment(false)}
          />
        )}

        {/* Cash Payment Modal - total is balance due when advance was applied */}
        {showCashPayment && (
          <CashPaymentModal
            total={balanceDue}
            onComplete={handleCashPaymentComplete}
            onClose={() => setShowCashPayment(false)}
          />
        )}

        {/* Variant Selection Modal - shown when product belongs to a variant group */}
        {pendingVariantSelection && (
          <VariantSelectionModal
            groupName={pendingVariantSelection.groupName}
            variants={pendingVariantSelection.variants}
            cart={cart}
            onSubmit={confirmVariantSelection}
            onCancel={cancelVariantSelection}
          />
        )}

        {/* Batch Selection Modal - shown when product has multiple batches with different prices */}
        {pendingBatchSelection && (
          <BatchSelectionModal
            product={pendingBatchSelection.product}
            batches={pendingBatchSelection.batches}
            cart={cart}
            onSelect={(batch) => confirmBatchSelection(pendingBatchSelection.product, batch)}
            onCancel={cancelBatchSelection}
          />
        )}
      </div>
    </div>
  )
}

export default POSPage
